import {
  ArrowsLeftRight,
  ArrowClockwise,
  Phone,
  Clock,
  Robot,
} from '@phosphor-icons/react';
import { formatDuration, intervalToDuration } from 'date-fns';
import { useMemo } from 'react';
import { Badge } from 'src/components/Badge';
import { Button } from 'src/components/Button';
import {
  formatValue,
  statusToBadgeVariant,
  statusToLabel,
} from 'src/components/callAnalysis/callAnalysis.helpers';
import { CopyableField } from 'src/components/CopyableField';
import { Loading } from 'src/components/Loading';
import { TranscriptDisplay } from 'src/components/TranscriptDisplay';
import { NotificationContextState } from 'src/contexts/NotificationContext';
import {
  PostCallAnalysisResultItem,
  PostCallAnalysisResultItemStatus,
} from 'src/interfaces/agent.interface';
import { Call } from 'src/interfaces/call.interface';
import { parseTranscript } from 'src/utils/transcript';

export type TestResultPanelProps = {
  testCall: Call;
  agentName: string;
  result: PostCallAnalysisResultItem | null;
  isEvaluating: boolean;
  canRetest: boolean;
  onChangeTestCall: () => void;
  onRetest: () => void;
  notification: NotificationContextState;
};

export const TestResultPanel = ({
  testCall,
  agentName,
  result,
  isEvaluating,
  canRetest,
  onChangeTestCall,
  onRetest,
  notification,
}: TestResultPanelProps) => {
  const startDate = testCall.start_time
    ? new Date(Number(testCall.start_time) * 1000)
    : null;
  const endDate = testCall.end_time
    ? new Date(Number(testCall.end_time) * 1000)
    : null;

  const duration =
    startDate && endDate
      ? formatDuration(intervalToDuration({ start: startDate, end: endDate }), {
          delimiter: ', ',
        }) || '0s'
      : '-';

  const isExtracted =
    result?.status === PostCallAnalysisResultItemStatus.EXTRACTED;
  const rationale =
    typeof result?.rationale === 'string' && result.rationale.trim() !== ''
      ? result.rationale
      : null;

  const transcriptMessages = useMemo(() => {
    if (!testCall?.transcript) return [];
    return parseTranscript(testCall.transcript);
  }, [testCall]);

  return (
    <div className="w-96 flex flex-col bg-neutral-50 border-l border-neutral-200">
      <div className="flex-1 overflow-auto p-4 space-y-4">
        {/* Test Call Info */}
        <div className="space-y-2">
          <span className="text-xs text-neutral-500 uppercase tracking-wide flex items-center gap-2">
            Test Call
          </span>
          <div className="p-3 rounded-lg border border-neutral-200 bg-neutral-0 space-y-2">
            <div className="flex items-center gap-2 text-sm font-medium text-neutral-700">
              <Phone className="h-3.5 w-3.5 text-neutral-400" />
              <CopyableField
                value={testCall.id}
                notification={notification}
                className="min-w-0"
              />
            </div>
            <div className="flex items-center gap-2 text-xs text-neutral-500">
              <Robot className="h-3 w-3" />
              {agentName}
            </div>
            <div className="flex items-center gap-2 text-xs text-neutral-500">
              <Clock className="h-3 w-3" />
              {duration}
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2">
          <Button
            variant="outlined"
            size="small"
            className="flex-1 gap-2"
            onClick={onChangeTestCall}
          >
            <ArrowsLeftRight className="h-4 w-4" />
            Change
          </Button>
          <Button
            variant="outlined"
            size="small"
            className="flex-1 gap-2"
            onClick={onRetest}
            disabled={isEvaluating || !canRetest}
          >
            <ArrowClockwise
              className={`h-4 w-4 ${isEvaluating ? 'animate-spin' : ''}`}
            />
            Re-test
          </Button>
        </div>

        {/* Divider */}
        <hr className="border-neutral-200" />

        {/* Insight Result */}
        <div className="space-y-2">
          <span className="text-xs text-neutral-500 uppercase tracking-wide">
            Insight Result
          </span>

          {isEvaluating ? (
            <div className="flex items-center justify-center py-8">
              <Loading size="6" />
            </div>
          ) : result ? (
            <div className="bg-neutral-0 border border-neutral-200 rounded-lg p-4">
              {isExtracted ? (
                // Extracted: Show only the value
                <div className="text-neutral-700">
                  {formatValue(result.value)}
                </div>
              ) : (
                // Not extracted: Show status badge and rationale
                <div className="space-y-3">
                  <Badge
                    variant={statusToBadgeVariant(result.status)}
                    className="rounded-full px-4"
                  >
                    {statusToLabel(result.status)}
                  </Badge>
                  {rationale && (
                    <p className="text-sm text-neutral-600 italic">
                      {rationale}
                    </p>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="bg-neutral-0 border border-neutral-200 rounded-lg p-4 text-sm text-neutral-400 text-center">
              Click Re-test to evaluate the insight
            </div>
          )}
        </div>

        {/* Divider */}
        <hr className="border-neutral-200" />

        {/* Transcript */}
        <div className="space-y-2">
          <span className="text-xs text-neutral-500 uppercase tracking-wide">
            Transcript
          </span>
          <TranscriptDisplay
            messages={transcriptMessages}
            rawTranscript={
              transcriptMessages.length === 0 ? testCall.transcript : undefined
            }
            className="max-h-80"
            emptyStateMessage="No transcript available for this call."
          />
        </div>
      </div>
    </div>
  );
};
